import { dehydrate, QueryClient } from '@tanstack/react-query';
import { Partner as PartnerPage } from 'components/pages/Partner';
import { fetchPartner, PARTNER_PAGE_KEY } from 'hooks/pages/usePartner';
import { fetchPartners } from 'hooks/pages/usePartners';
import { env } from 'lib/constants/env';
import type { PageContext } from 'types/common/PageContext';
import {
  blocksBuildTimeResolver,
  prefetchCommonData,
  prefetchReviewsData,
} from 'utils/buildTime';
import { fetchWithSentry } from 'utils/pageTemplates/fetchWithSentry';

export function partnerPage() {
  async function getStaticProps(context: PageContext) {
    const queryClient = new QueryClient();

    const partnerPageData = await fetchWithSentry({
      queryClient,
      queryKey: [PARTNER_PAGE_KEY, context.params.slug],
      queryFn: fetchPartner,
      tags: {
        template: 'partnerPage',
        slug: context.params.slug,
        api: 'sanity',
      },
    });

    if (!partnerPageData || !partnerPageData.quote) {
      return {
        notFound: true,
        revalidate: env.ISR_TTL_SECONDS,
      };
    }

    await prefetchCommonData(queryClient);
    await prefetchReviewsData(queryClient);
    await blocksBuildTimeResolver(queryClient, partnerPageData.blocks);

    return {
      revalidate: env.ISR_TTL_SECONDS,
      props: {
        dehydratedState: dehydrate(queryClient),
      },
    };
  }

  const getStaticPaths = async () => {
    const partners = await fetchPartners();
    const paths = partners.map((partner) => ({
      params: { slug: partner.slug },
    }));

    const pathToRender = paths.find(
      (path) => path.params.slug === env.TEST_PARTNER_PAGE,
    );

    return { paths: pathToRender ? [pathToRender] : [], fallback: 'blocking' };
  };

  const PartnerPageComponent = () => <PartnerPage />;

  return {
    getStaticProps,
    getStaticPaths,
    PartnerPageComponent,
  };
}
